import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { useParams, useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { Link } from 'ui/base/link';
import { LinkClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { CorePage } from 'ui/base/page/core_page/core_page';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';

import { Redirect } from 'pages/core/redirect/redirect';

import { Optional, ResultKind } from 'services/base/types';
import { BasePath, CoreBasePath } from 'services/base/page_path';
import { HistoryService } from 'services/base/history';

import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { SessionService } from 'services/accounts/session_service';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import baseStyles from './more.module.css';
import moreMessages from './more_messages';

type MorePageStyles = {
  link: string;
  headerContainer: string;
  headerRow: string;
  container: string;
  textLabel: string;
  pangolinName: string;
  row: string;
  firstRow: string;
  managePangolinIcon: string;
  addIcon: string;
  settingIcon: string;
  userManualIcon: string;
  newsIcon: string;
  supportIcon: string;
  signOutIcon: string;
  lastRow: string;
  option: string;
  selected: string;
  pangolinRow: string;
};

const themedStyles: ThemedStyles<MorePageStyles> = {
  [Theme.DAYLIGHT]: {
    link: baseStyles.baseLink,
    headerContainer: baseStyles.baseHeaderContainer,
    headerRow: baseStyles.baseHeaderRow,
    container: baseStyles.baseContainer,
    textLabel: baseStyles.daylightTextLabel,
    pangolinName: baseStyles.daylightPangolinName,
    row: baseStyles.baseRow,
    firstRow: baseStyles.baseFirstRow,
    managePangolinIcon: baseStyles.daylightManagePangolinIcon,
    addIcon: baseStyles.daylightAddIcon,
    settingIcon: baseStyles.daylightSettingIcon,
    userManualIcon: baseStyles.daylightUserManualIcon,
    newsIcon: baseStyles.daylightNewsIcon,
    supportIcon: baseStyles.daylightSupportIcon,
    signOutIcon: baseStyles.daylightSignOutIcon,
    lastRow: baseStyles.baseLastRow,
    option: baseStyles.daylightOption,
    selected: baseStyles.daylightSelected,
    pangolinRow: baseStyles.basePangolinRow,
  },
  [Theme.MIDNIGHT]: {
    link: baseStyles.baseLink,
    headerContainer: baseStyles.baseHeaderContainer,
    headerRow: baseStyles.baseHeaderRow,
    container: baseStyles.baseContainer,
    textLabel: baseStyles.midnightTextLabel,
    pangolinName: baseStyles.midnightPangolinName,
    row: baseStyles.baseRow,
    firstRow: baseStyles.baseFirstRow,
    managePangolinIcon: baseStyles.midnightManagePangolinIcon,
    addIcon: baseStyles.midnightAddIcon,
    settingIcon: baseStyles.midnightSettingIcon,
    userManualIcon: baseStyles.midnightUserManualIcon,
    newsIcon: baseStyles.midnightNewsIcon,
    supportIcon: baseStyles.midnightSupportIcon,
    signOutIcon: baseStyles.midnightSignOutIcon,
    lastRow: baseStyles.baseLastRow,
    option: baseStyles.midnightOption,
    selected: baseStyles.midnightSelected,
    pangolinRow: baseStyles.basePangolinRow,
  },
};

type PangolinOptionProps = {
  active: Pangolin;
  target: Pangolin;
  location: CoreBasePath;
};

const PangolinOption = observer(({ active, target, location }: PangolinOptionProps) => {
  const styles = useStyles(themedStyles);

  const chosen = target.id === active.id;
  const path = { kind: location, pangolinID: target.id };
  const className = classNames(chosen ? styles.selected : styles.option);

  const onClick = React.useCallback((event: LinkClickEvent) => {
    if (chosen) event.preventDefault();
  }, [chosen]);

  return (
    <Link path={path} className={className} onClick={onClick} replace>
      <ShieldIcon
        colorCode={target.colorCode}
        onlineStatus={target.onlineStatus}
        size={ShieldSize.SMALL}
      />
      <div className={styles.pangolinName}>
        {target.nickname}
      </div>
    </Link>
  );
});

function chunkAndPad<T>(objects: T[], chunkSize: number): Optional<T>[][] {
  const chunks: Optional<T>[][] = [];
  let curr: Optional<T>[] = [];
  for (let i=0, j=0; i<objects.length || j !== 0; i++, j = (j + 1) % chunkSize) {
    if (j === 0) {
      curr = [];
      chunks.push(curr);
    }
    if (i < objects.length) {
      curr.push(objects[i]);
    } else {
      curr.push(undefined);
    }
  }
  return chunks;
}

type PangolinSelectorChoicesProps = {
  accounts: IAccountsDomain,
  pangolin: Pangolin;
  location: CoreBasePath;
};
const PangolinSelectorChoices = observer(({ accounts, pangolin, location }: PangolinSelectorChoicesProps) => {
  const styles = useStyles(themedStyles);
  const selectorRows = chunkAndPad(accounts.pangolins.values, 4);
  return (
    <>
      {selectorRows.map((items, rowNumber) => (
        <div className={styles.pangolinRow} key={rowNumber}>
          {items.map((target, index) => (
            target != null ? (
              <PangolinOption
                key={`option-pangolin-${target.id}`}
                active={pangolin}
                target={target}
                location={location}
              />
            ) : (
              <div
                key={`option-blank-${index}`}
                className={styles.option}
              />
            )
          ))}
        </div>
      ))}
    </>
  );
});

type BaseMoreProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};
export const BaseMore = observer(({ accounts, pangolin } : BaseMoreProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const handleLogout = React.useCallback(action(async() => {
    const result = await SessionService.logout(accounts);
    if (result.kind === ResultKind.OK || result.kind === ResultKind.USER_ERROR) {
      HistoryService.clear(history);
      HistoryService.replace(history, { kind: BasePath.INTRO });
    }
  }), [accounts, history]);

  return (
    <CorePage pangolin={pangolin} location={BasePath.MORE}>
      <div>
        <div className={styles.headerContainer}>
          <div className={styles.headerRow}>
            <PangolinSelectorChoices
              accounts={accounts}
              pangolin={pangolin}
              location={BasePath.MORE}
            />
          </div>
        </div>
        { accounts.pangolins.size === 1 && pangolin != null &&
          <Link path={{ kind: BasePath.MANAGE_PANGOLIN_VIEW, pangolinID: pangolin.id }} className={styles.link}>
            <div className={styles.firstRow}>
              <div className={styles.textLabel}>
                {moreMessages.managePangolins()}
              </div>
              <div>
                <div className={styles.managePangolinIcon}/>
              </div>
            </div>
          </Link>}
        { accounts.pangolins.size > 1 &&
          <Link path={{ kind: BasePath.MANAGE_PANGOLIN_LIST }} className={styles.link}>
            <div className={styles.firstRow}>
              <div className={styles.textLabel}>
                {moreMessages.managePangolins()}
              </div>
              <div>
                <div className={styles.managePangolinIcon}/>
              </div>
            </div>
          </Link>}
        <Link path={{ kind: BasePath.ADD_PANGOLIN_SETUP }} className={styles.link}>
          <div className={styles.row}>
            <div className={styles.textLabel}>
              {moreMessages.addPangolin()}
            </div>
            <div>
              <div className={styles.addIcon}/>
            </div>
          </div>
        </Link>
        <Link path={{ kind: BasePath.SETTINGS }} className={styles.link}>
          <div className={styles.row}>
            <div className={styles.textLabel}>
              {moreMessages.settings()}
            </div>
            <div>
              <div className={styles.settingIcon}/>
            </div>
          </div>
        </Link>
        <a
          href='https://pangolinsecured.com/pages/user-manual'
          target='_blank'
          rel='noopener noreferrer'
          className={styles.link}
        >
          <div className={styles.row}>
            <div className={styles.textLabel}>
              {moreMessages.userManual()}
            </div>
            <div>
              <div className={styles.userManualIcon}/>
            </div>
          </div>
        </a>
        <a
          href='https://pangolinsecured.com/pages/update-history'
          target='_blank'
          rel='noopener noreferrer'
          className={styles.link}
        >
          <div className={styles.row}>
            <div className={styles.textLabel}>
              {moreMessages.updateHistory()}
            </div>
            <div>
              <div className={styles.newsIcon}/>
            </div>
          </div>
        </a>
        <a
          href='https://pangolinsecured.com/blogs/news'
          target='_blank'
          rel='noopener noreferrer'
          className={styles.link}
        >
          <div className={styles.row}>
            <div className={styles.textLabel}>
              {moreMessages.news()}
            </div>
            <div>
              <div className={styles.newsIcon}/>
            </div>
          </div>
        </a>
        <a
          href='https://pangolinsecured.com/pages/contact-us'
          target='_blank'
          rel='noopener noreferrer'
          className={styles.link}
        >
          <div className={styles.row}>
            <div className={styles.textLabel}>
              {moreMessages.support()}
            </div>
            <div>
              <div className={styles.supportIcon}/>
            </div>
          </div>
        </a>
        <div className={styles.lastRow} onClick={handleLogout}>
          <div className={styles.textLabel}>
            {moreMessages.signOut()}
          </div>
          <div>
            <div className={styles.signOutIcon}/>
          </div>
        </div>
      </div>
    </CorePage>
  );
});

export const More = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return (
    <BaseMore
      accounts={accounts}
      pangolin={pangolin}
    />
  );
});
